import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, AlertTriangle, User, MapPin,
  FileText, Calendar, Briefcase, Hash, Shield, Info,
  CheckCircle2, Copy,
} from 'lucide-react';

import { laudoApi } from '../../services/laudoApi';

/**
 * ============================================================
 * COMPONENTE: NovaVistoriaStandalone
 * ------------------------------------------------------------
 * Criação de vistoria avulsa (fora do fluxo de laudo).
 *   Rota: /vistorias/nova
 *
 * FLUXO:
 *   1. Usuário preenche dados básicos
 *   2. Envia via laudoApi.salvarModulo()
 *   3. Servidor retorna o ID da nova vistoria
 *   4. Redireciona para /vistorias/:id
 *   5. VistoriaStandalone carrega e abre VistoriaRegiaoMain
 *
 * DIFERENÇA em relação a `NovaVistoria`:
 *   - `NovaVistoria`      → editor embutido em /laudos/:codigo/vistoria
 *   - `NovaVistoriaStandalone` → criação standalone em /vistorias/nova
 *
 * VERSÃO: 1.0.0
 * ============================================================
 */

const FORM_INICIAL = {
  titulo: '',
  processo: '',
  endereco: '',
  perito: '',
  dataVistoria: '',
  observacoes: '',
};

const REGEX_PROCESSO_CNJ = /^\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}$/;

/* =========================
   COMPONENTE
========================= */

export default function NovaVistoriaStandalone() {
  const navigate = useNavigate();

  const [form, setForm] = useState(FORM_INICIAL);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [sucesso, setSucesso] = useState(null);

  const atualizar = (campo, valor) =>
    setForm(prev => ({ ...prev, [campo]: valor }));

  const temDadosPreenchidos = () =>
    Object.values(form).some(v => String(v || '').trim().length > 0);

  /* ---------- validação ---------- */

  const validar = () => {
    if (!form.titulo.trim()) {
      return 'Título da vistoria é obrigatório.';
    }
    if (form.titulo.trim().length < 5) {
      return 'Título deve ter ao menos 5 caracteres.';
    }
    if (!form.endereco.trim()) {
      return 'Endereço do imóvel é obrigatório.';
    }
    if (form.processo && !REGEX_PROCESSO_CNJ.test(form.processo.trim())) {
      return 'Processo deve seguir o formato CNJ: 0000000-00.0000.0.00.0000';
    }
    return null;
  };

  /* ---------- envio ---------- */

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setErro(null);
    setSucesso(null);

    const problema = validar();
    if (problema) {
      setErro(problema);
      return;
    }

    setSalvando(true);

    try {
      const resposta = await laudoApi.salvarModulo({
        modulo: 'NOVA_VISTORIA',
        tipo_registro: 'CRIACAO',
        status: 'ABERTA',
        titulo: form.titulo.trim(),
        processo: form.processo.trim() || null,
        endereco: form.endereco.trim(),
        perito: form.perito.trim() || null,
        dataVistoria: form.dataVistoria || null,
        observacoes: form.observacoes.trim() || null,
        criadoEm: new Date().toISOString(),
      });

      // Tenta extrair ID de várias formas (compatibilidade com diferentes backends)
      const novoId =
        resposta?.id ||
        resposta?.vistoria_id ||
        resposta?.data?.id ||
        resposta?.data?.vistoria_id ||
        resposta?.laudo_id;

      if (!novoId) {
        throw new Error(
          'Servidor não retornou o ID da vistoria criada. Verifique o backend.'
        );
      }

      setSucesso({
        id: novoId,
        mensagem: 'Vistoria criada com sucesso. Redirecionando...',
      });

      // Aguarda 1s para o usuário ver o feedback, depois navega
      setTimeout(() => {
        navigate(`/vistorias/${novoId}`);
      }, 1000);

    } catch (err) {
      if (err._enfileirado) {
        setErro(
          'Sem conexão — vistoria salva localmente na fila. ' +
          'Sincronize ao restabelecer a conexão para obter o ID e abrir o editor.'
        );
      } else {
        setErro(err.message || 'Falha ao criar vistoria.');
      }
    } finally {
      setSalvando(false);
    }
  };

  /* ---------- cancelar / voltar ---------- */

  const voltar = () => {
    if (temDadosPreenchidos() && !salvando) {
      const ok = window.confirm(
        'Existem dados não salvos. Deseja realmente sair?'
      );
      if (!ok) return;
    }
    navigate('/vistorias');
  };

  const limparForm = () => {
    if (temDadosPreenchidos()) {
      const ok = window.confirm('Limpar todos os campos?');
      if (!ok) return;
    }
    setForm(FORM_INICIAL);
    setErro(null);
    setSucesso(null);
  };

  /* ---------- cópia de ID de sucesso ---------- */

  const copiarIdSucesso = async () => {
    if (!sucesso?.id) return;
    try {
      await navigator.clipboard.writeText(String(sucesso.id));
    } catch {
      /* silencioso */
    }
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-black italic uppercase">

      <main className="max-w-3xl mx-auto p-8 lg:p-12 space-y-8">

        {/* HEADER */}
        <header className="space-y-4">
          <button
            type="button"
            onClick={voltar}
            disabled={salvando}
            className="flex items-center gap-2 text-slate-400 hover:text-white text-[10px] tracking-widest transition-all disabled:opacity-50"
          >
            <ArrowLeft size={14} />
            VOLTAR PARA AGENDA
          </button>

          <div className="flex items-center gap-3 text-amber-400">
            <Calendar size={24} />
            <span className="text-[10px] tracking-[0.5em] text-slate-500">
              NOVA VISTORIA · REGISTRO AVULSO
            </span>
          </div>

          <h1 className="text-3xl lg:text-4xl text-white tracking-tight leading-none">
            Criar Vistoria de Campo
          </h1>

          <p className="text-[10px] text-slate-500 tracking-[0.3em] max-w-2xl leading-relaxed normal-case">
            Os dados básicos são suficientes para iniciar. Depois de criada,
            a vistoria será aberta no editor completo com os 6 fatores NBR 14653-2.
          </p>
        </header>

        {/* ERRO */}
        {erro && (
          <div className="bg-red-950/50 border-2 border-red-500/30 rounded-2xl p-6 flex items-start gap-4">
            <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
            <span className="text-red-200 text-[11px] tracking-widest leading-relaxed normal-case flex-1">
              {erro}
            </span>
          </div>
        )}

        {/* SUCESSO */}
        {sucesso && (
          <div className="bg-emerald-950/50 border-2 border-emerald-500/30 rounded-2xl p-6 flex items-start gap-4">
            <CheckCircle2 size={20} className="text-emerald-400 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <span className="text-emerald-200 text-[11px] tracking-widest leading-relaxed normal-case block">
                {sucesso.mensagem}
              </span>
              <div className="flex items-center gap-2 text-[10px] text-emerald-300/70 font-mono normal-case">
                <span>ID: {sucesso.id}</span>
                <button
                  type="button"
                  onClick={copiarIdSucesso}
                  className="w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all"
                  title="Copiar ID"
                >
                  <Copy size={10} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FORM */}
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* TÍTULO */}
          <Field label="Título da Vistoria" obrigatorio icon={FileText}>
            <input
              type="text"
              value={form.titulo}
              onChange={(e) => atualizar('titulo', e.target.value)}
              placeholder="Ex: Vistoria Imóvel Centro — Ref. 2026-0042"
              className={inputBase}
              disabled={salvando}
              autoFocus
              maxLength={120}
            />
            <span className="text-[9px] text-slate-600 tracking-widest normal-case block mt-1">
              {form.titulo.length} / 120 caracteres
            </span>
          </Field>

          {/* PROCESSO + PERITO */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Processo Judicial" icon={Hash}>
              <input
                type="text"
                value={form.processo}
                onChange={(e) => {
                  // Máscara automática de processo CNJ
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 20);
                  let formatado = digits;
                  if (digits.length > 7 && digits.length <= 9) {
                    formatado = `${digits.slice(0, 7)}-${digits.slice(7)}`;
                  } else if (digits.length > 9 && digits.length <= 13) {
                    formatado = `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9)}`;
                  } else if (digits.length > 13 && digits.length <= 14) {
                    formatado = `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9, 13)}.${digits.slice(13)}`;
                  } else if (digits.length > 14 && digits.length <= 15) {
                    formatado = `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9, 13)}.${digits.slice(13, 14)}.${digits.slice(14)}`;
                  } else if (digits.length > 15 && digits.length <= 17) {
                    formatado = `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9, 13)}.${digits.slice(13, 14)}.${digits.slice(14, 16)}.${digits.slice(16)}`;
                  } else if (digits.length > 17) {
                    formatado = `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9, 13)}.${digits.slice(13, 14)}.${digits.slice(14, 16)}.${digits.slice(16, 20)}`;
                  }
                  atualizar('processo', formatado);
                }}
                placeholder="0000000-00.0000.0.00.0000"
                className={inputBase + ' font-mono'}
                disabled={salvando}
                maxLength={25}
              />
              {form.processo && !REGEX_PROCESSO_CNJ.test(form.processo) && (
                <span className="text-[9px] text-amber-400 tracking-widest normal-case block mt-1 flex items-center gap-1">
                  <AlertTriangle size={10} />
                  Formato CNJ incompleto
                </span>
              )}
              {form.processo && REGEX_PROCESSO_CNJ.test(form.processo) && (
                <span className="text-[9px] text-emerald-400 tracking-widest normal-case block mt-1 flex items-center gap-1">
                  <CheckCircle2 size={10} />
                  Formato válido
                </span>
              )}
            </Field>

            <Field label="Perito Responsável" icon={User}>
              <input
                type="text"
                value={form.perito}
                onChange={(e) => atualizar('perito', e.target.value)}
                placeholder="Nome do perito"
                className={inputBase}
                disabled={salvando}
                maxLength={80}
              />
            </Field>
          </div>

          {/* ENDEREÇO */}
          <Field label="Endereço do Imóvel" obrigatorio icon={MapPin}>
            <input
              type="text"
              value={form.endereco}
              onChange={(e) => atualizar('endereco', e.target.value)}
              placeholder="Rua, número, bairro, cidade/UF"
              className={inputBase}
              disabled={salvando}
              maxLength={200}
            />
          </Field>

          {/* DATA PREVISTA */}
          <Field label="Data Prevista da Vistoria" icon={Calendar}>
            <input
              type="datetime-local"
              value={form.dataVistoria}
              onChange={(e) => atualizar('dataVistoria', e.target.value)}
              className={inputBase + ' font-mono'}
              disabled={salvando}
            />
          </Field>

          {/* OBSERVAÇÕES */}
          <Field label="Observações" icon={Briefcase}>
            <textarea
              value={form.observacoes}
              onChange={(e) => atualizar('observacoes', e.target.value)}
              placeholder="Anotações relevantes para a diligência (contato do proprietário, particularidades do local, etc.)..."
              rows={4}
              className={inputBase + ' resize-none py-4 leading-relaxed normal-case min-h-[120px]'}
              disabled={salvando}
              maxLength={1000}
            />
            <span className="text-[9px] text-slate-600 tracking-widest normal-case block mt-1">
              {form.observacoes.length} / 1000 caracteres
            </span>
          </Field>

          {/* NOTA NORMATIVA */}
          <div className="bg-slate-900 border border-white/5 rounded-2xl p-5 flex items-start gap-4">
            <Shield size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="space-y-2 flex-1">
              <span className="text-[9px] text-amber-400 tracking-[0.3em] block">
                INTEGRIDADE PGLP
              </span>
              <p className="text-[10px] text-slate-400 tracking-wide leading-relaxed normal-case">
                Ao criar esta vistoria, um registro imutável será gerado no backend
                com timestamp e ID único. Todos os dados coletados em campo
                (evidências, fatores, hash SHA-256) serão vinculados a esse ID
                conforme <strong className="text-slate-300">NBR 14653-2:2011</strong>.
              </p>
            </div>
          </div>

          {/* AÇÕES */}
          <div className="flex flex-wrap justify-between items-center gap-3 pt-6 border-t border-white/5">
            <button
              type="button"
              onClick={limparForm}
              disabled={salvando || !temDadosPreenchidos()}
              className="px-6 py-3 rounded-2xl text-slate-500 hover:text-red-400 text-[10px] tracking-widest transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              LIMPAR CAMPOS
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={voltar}
                disabled={salvando}
                className="px-6 py-4 rounded-2xl bg-white/5 border border-white/10 text-white text-[11px] tracking-widest hover:bg-white/10 transition-all disabled:opacity-50"
              >
                CANCELAR
              </button>

              <button
                type="submit"
                disabled={salvando || !!sucesso}
                className="bg-amber-400 text-slate-950 px-8 py-4 rounded-2xl text-[11px] tracking-widest flex items-center gap-3 hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {salvando ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    CRIANDO...
                  </>
                ) : sucesso ? (
                  <>
                    <CheckCircle2 size={16} />
                    CRIADA
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    CRIAR E ABRIR
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* RODAPÉ INFORMATIVO */}
        <footer className="pt-6 border-t border-white/5 flex items-start gap-4">
          <Info size={16} className="text-slate-500 shrink-0 mt-0.5" />
          <p className="text-[10px] text-slate-500 tracking-wider leading-relaxed normal-case">
            Após criar, você será redirecionado para o editor completo da vistoria
            onde poderá preencher os 6 fatores regionais, capturar evidências
            fotográficas com marca d'água e geolocalização, e selar o laudo
            com hash SHA-256.
          </p>
        </footer>
      </main>
    </div>
  );
}

/* =========================
   HELPERS
========================= */

const inputBase =
  'w-full h-12 bg-slate-900 border border-white/10 rounded-2xl px-4 ' +
  'text-white text-[12px] outline-none focus:border-amber-400 ' +
  'transition-all placeholder:text-slate-600 disabled:opacity-50';

function Field({ label, icon: Icon, obrigatorio, children }) {
  return (
    <div className="space-y-2">
      <label className="text-[9px] text-slate-500 tracking-[0.3em] flex items-center gap-2">
        {Icon && <Icon size={12} />}
        {label}
        {obrigatorio && (
          <span className="text-amber-400 text-[10px] leading-none">*</span>
        )}
      </label>
      {children}
    </div>
  );
}